import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    Trophy, Plus, Users, Calendar, Edit, Trash2, RefreshCw, Eye, EyeOff,
    Crown, Clock, CheckCircle, AlertCircle, Copy, ExternalLink,
    Medal, ChevronDown, UserMinus, PlusCircle, MinusCircle, Search, UserPlus, RotateCcw, QrCode
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCompetitionsPage() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    const [competitions, setCompetitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedComp, setSelectedComp] = useState(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showAdjustDialog, setShowAdjustDialog] = useState(false);
    const [showAddDayDialog, setShowAddDayDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [removedUsers, setRemovedUsers] = useState([]);
    const [showRemovedUsers, setShowRemovedUsers] = useState(false);

    // Form state for create/edit
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        meeting_id: '',
        meeting_name: '',
        start_date: '',
        end_date: '',
        deadline_minutes: 30,
        is_featured: false,
        featured_description: '',
        featured_countdown_date: '',
        is_festival: false
    });

    // Add day form state
    const [addDayData, setAddDayData] = useState({
        date: '',
        meeting_id: '',
        meeting_name: ''
    });

    // Adjustment form
    const [adjustmentData, setAdjustmentData] = useState({
        user_id: '',
        points_adjustment: 0,
        reason: ''
    });

    // Daily winner management
    const [showDailyWinnerDialog, setShowDailyWinnerDialog] = useState(false);
    const [selectedDayForWinner, setSelectedDayForWinner] = useState(null);
    const [dailyWinnerUserId, setDailyWinnerUserId] = useState('');

    // Meetings for selection
    const [meetings, setMeetings] = useState([]);
    const [loadingMeetings, setLoadingMeetings] = useState(false);
    const [meetingSearch, setMeetingSearch] = useState('');

    useEffect(() => {
        if (!isAuthenticated || !user?.is_admin) {
            toast.error('Admin access required');
            navigate('/competitions');
            return;
        }
        fetchCompetitions();
    }, [isAuthenticated, user, navigate]);

    const fetchCompetitions = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/competitions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompetitions(res.data.competitions || []);
        } catch (err) {
            console.error('Failed to fetch competitions:', err);
            toast.error('Failed to load competitions');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompetitionDetail = async (compId) => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/competitions/${compId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedComp(res.data);
        } catch (err) {
            console.error('Failed to fetch competition:', err);
            toast.error('Failed to load competition details');
        }
    };

    const searchMeetings = async (searchDate) => {
        if (!searchDate) return;
        setLoadingMeetings(true);
        try {
            const token = getToken();
            // Format date for API (DD-MM-YYYY) - HTML input gives YYYY-MM-DD
            const parts = searchDate.split('-');
            const formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            
            console.log('Searching meetings for date:', formattedDate);
            
            // Fetch UK and IRE meetings separately then combine
            const [ukRes, ireRes] = await Promise.all([
                axios.get(`${API_URL}/api/racing/stages`, {
                    params: { from_date: formattedDate, to_date: formattedDate, country: 'UK' },
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                }),
                axios.get(`${API_URL}/api/racing/stages`, {
                    params: { from_date: formattedDate, to_date: formattedDate, country: 'IRE' },
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                })
            ]);
            
            const ukItems = ukRes.data.items || [];
            const ireItems = ireRes.data.items || [];
            const allItems = [...ukItems, ...ireItems];
            
            console.log('Meetings found:', allItems.length);
            setMeetings(allItems);
            
            if (allItems.length === 0) {
                toast.info('No meetings found for this date. Try a different date.');
            }
        } catch (err) {
            console.error('Failed to fetch meetings:', err);
            toast.error('Failed to load meetings');
        } finally {
            setLoadingMeetings(false);
        }
    };

    const handleCreate = async () => {
        if (!formData.name || !formData.meeting_id) {
            toast.error('Please fill in required fields');
            return;
        }
        
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(`${API_URL}/api/admin/competitions`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Competition created! Invite code: ${res.data.competition.invite_code}`);
            setShowCreateDialog(false);
            resetForm();
            fetchCompetitions();
        } catch (err) {
            console.error('Failed to create competition:', err);
            toast.error(err.response?.data?.detail || 'Failed to create competition');
        } finally {
            setProcessing(false);
        }
    };

    const handleUpdate = async () => {
        if (!selectedComp?.competition?.id) return;
        
        setProcessing(true);
        try {
            const token = getToken();
            await axios.put(
                `${API_URL}/api/admin/competitions/${selectedComp.competition.id}`,
                {
                    name: formData.name || undefined,
                    description: formData.description || undefined,
                    deadline_minutes: formData.deadline_minutes || undefined,
                    is_featured: formData.is_featured,
                    featured_description: formData.featured_description || null,
                    featured_countdown_date: formData.featured_countdown_date || null,
                    status: formData.status || undefined,
                    is_festival: formData.is_festival
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Competition updated');
            setShowEditDialog(false);
            fetchCompetitions();
            if (selectedComp) fetchCompetitionDetail(selectedComp.competition.id);
        } catch (err) {
            console.error('Failed to update:', err);
            toast.error('Failed to update competition');
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async (compId) => {
        try {
            const token = getToken();
            await axios.delete(`${API_URL}/api/admin/competitions/${compId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Competition deleted');
            setSelectedComp(null);
            fetchCompetitions();
        } catch (err) {
            console.error('Failed to delete:', err);
            toast.error('Failed to delete competition');
        }
    };

    const handleToggleVisibility = async (compId) => {
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/toggle-visibility`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            fetchCompetitions();
            if (selectedComp?.competition?.id === compId) {
                fetchCompetitionDetail(compId);
            }
        } catch (err) {
            console.error('Failed to toggle visibility:', err);
            toast.error('Failed to toggle visibility');
        }
    };

    const handleAddDay = async () => {
        if (!selectedComp?.competition?.id || !addDayData.meeting_id) {
            toast.error('Please select a meeting');
            return;
        }
        
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${selectedComp.competition.id}/add-day`,
                addDayData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`${res.data.message} - ${res.data.races_added} races added`);
            setShowAddDayDialog(false);
            setAddDayData({ date: '', meeting_id: '', meeting_name: '' });
            setMeetings([]);
            fetchCompetitionDetail(selectedComp.competition.id);
            fetchCompetitions();
        } catch (err) {
            console.error('Failed to add day:', err);
            toast.error(err.response?.data?.detail || 'Failed to add day');
        } finally {
            setProcessing(false);
        }
    };

    const handleRemoveDay = async (dayNumber) => {
        if (!selectedComp?.competition?.id) return;
        
        if (!confirm(`Are you sure you want to remove Day ${dayNumber}? This will delete all races and picks for this day.`)) {
            return;
        }
        
        setProcessing(true);
        try {
            const token = getToken();
            await axios.delete(
                `${API_URL}/api/admin/competitions/${selectedComp.competition.id}/days/${dayNumber}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Day ${dayNumber} removed`);
            fetchCompetitionDetail(selectedComp.competition.id);
            fetchCompetitions();
        } catch (err) {
            console.error('Failed to remove day:', err);
            toast.error('Failed to remove day');
        } finally {
            setProcessing(false);
        }
    };

    const handleRefreshRaces = async (compId) => {
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/refresh-races`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Refreshed ${res.data.races?.length || 0} races`);
            if (selectedComp) fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to refresh:', err);
            toast.error('Failed to refresh races');
        } finally {
            setProcessing(false);
        }
    };

    const handleProcessResults = async (compId, force = false) => {
        setProcessing(true);
        try {
            const token = getToken();
            const url = force 
                ? `${API_URL}/api/admin/competitions/${compId}/process-results?force=true`
                : `${API_URL}/api/admin/competitions/${compId}/process-results`;
            const res = await axios.post(
                url,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            if (selectedComp) fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to process results:', err);
            toast.error('Failed to process results');
        } finally {
            setProcessing(false);
        }
    };

    const handleBackfillPickSilks = async (compId) => {
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/backfill-pick-silks`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
        } catch (err) {
            console.error('Failed to backfill pick silks:', err);
            toast.error('Failed to backfill pick silks');
        } finally {
            setProcessing(false);
        }
    };

    const handleRemoveUser = async (compId, userId) => {
        try {
            const token = getToken();
            await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/entries/${userId}/remove`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('User removed (can be restored)');
            fetchCompetitionDetail(compId);
            // Refresh removed users list if it's open
            if (showRemovedUsers) {
                fetchRemovedUsers(compId);
            }
        } catch (err) {
            console.error('Failed to remove user:', err);
            toast.error('Failed to remove user');
        }
    };

    const fetchRemovedUsers = async (compId) => {
        try {
            const token = getToken();
            const res = await axios.get(
                `${API_URL}/api/admin/competitions/${compId}/removed-users`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setRemovedUsers(res.data.removed_users || []);
        } catch (err) {
            console.error('Failed to fetch removed users:', err);
            setRemovedUsers([]);
        }
    };

    const handleRestoreUser = async (compId, userId) => {
        try {
            const token = getToken();
            await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/entries/${userId}/restore`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('User restored successfully!');
            fetchCompetitionDetail(compId);
            fetchRemovedUsers(compId);
        } catch (err) {
            console.error('Failed to restore user:', err);
            toast.error('Failed to restore user');
        }
    };

    const handleAdjustScore = async () => {
        if (!adjustmentData.user_id || !adjustmentData.reason) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setProcessing(true);
        try {
            const token = getToken();
            await axios.post(
                `${API_URL}/api/admin/competitions/${selectedComp.competition.id}/adjust-score`,
                adjustmentData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Score adjusted');
            setShowAdjustDialog(false);
            setAdjustmentData({ user_id: '', points_adjustment: 0, reason: '' });
            fetchCompetitionDetail(selectedComp.competition.id);
        } catch (err) {
            console.error('Failed to adjust score:', err);
            toast.error('Failed to adjust score');
        } finally {
            setProcessing(false);
        }
    };

    const handleProcessSingleRace = async (compId, eventId) => {
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/process-race/${eventId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const result = res.data;
            if (result.status === 'success') {
                toast.success(`Result processed! Winner: ${result.winner}`);
            } else if (result.status === 'already_done') {
                toast.info('Race already processed');
            } else if (result.status === 'not_finished') {
                toast.warning(`Race not finished yet (${result.race_status})`);
            } else {
                toast.info(result.message);
            }
            fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to process race:', err);
            toast.error(err.response?.data?.detail || 'Failed to process race result');
        } finally {
            setProcessing(false);
        }
    };

    const handleRefreshAllResults = async (compId) => {
        if (!confirm('This will re-fetch all race results from the API and update SP values and points. Continue?')) {
            return;
        }
        
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/process-results?force=true`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message || 'All results refreshed!');
            fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to refresh results:', err);
            toast.error(err.response?.data?.detail || 'Failed to refresh results');
        } finally {
            setProcessing(false);
        }
    };

    const handleApproveDailyWinner = async (compId, date) => {
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/competitions/${compId}/daily-winner/${date}/approve`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Daily winner for ${date} approved!`);
            fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to approve winner:', err);
            toast.error('Failed to approve daily winner');
        } finally {
            setProcessing(false);
        }
    };

    const handleAddDailyWinner = async () => {
        if (!dailyWinnerUserId || !selectedDayForWinner || !selectedComp) {
            toast.error('Please select a player');
            return;
        }
        
        setProcessing(true);
        try {
            const token = getToken();
            const user = selectedComp.entries?.find(e => e.user_id === dailyWinnerUserId);
            
            await axios.post(
                `${API_URL}/api/admin/competitions/${selectedComp.competition.id}/daily-winners`,
                {
                    date: selectedDayForWinner,
                    user_id: dailyWinnerUserId,
                    user_name: user?.user_name || 'Unknown',
                    points: 0
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Daily winner added for ${new Date(selectedDayForWinner).toLocaleDateString()}`);
            setShowDailyWinnerDialog(false);
            setDailyWinnerUserId('');
            setSelectedDayForWinner(null);
            fetchCompetitionDetail(selectedComp.competition.id);
        } catch (err) {
            console.error('Failed to add daily winner:', err);
            toast.error(err.response?.data?.detail || 'Failed to add daily winner');
        } finally {
            setProcessing(false);
        }
    };

    const handleRemoveDailyWinner = async (compId, date) => {
        if (!confirm(`Remove daily winner for ${new Date(date).toLocaleDateString()}?`)) return;
        
        setProcessing(true);
        try {
            const token = getToken();
            await axios.delete(
                `${API_URL}/api/admin/competitions/${compId}/daily-winners/${date}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Daily winner removed');
            fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to remove daily winner:', err);
            toast.error(err.response?.data?.detail || 'Failed to remove daily winner');
        } finally {
            setProcessing(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            meeting_id: '',
            meeting_name: '',
            start_date: '',
            end_date: '',
            deadline_minutes: 30,
            is_featured: false,
            featured_description: '',
            featured_countdown_date: '',
            is_festival: false
        });
        setMeetings([]);
    };

    const handleDeleteRace = async (compId, eventId) => {
        setProcessing(true);
        try {
            const token = getToken();
            await axios.delete(
                `${API_URL}/api/admin/competitions/${compId}/races/${eventId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Race removed from competition');
            fetchCompetitionDetail(compId);
        } catch (err) {
            console.error('Failed to delete race:', err);
            toast.error('Failed to delete race');
        } finally {
            setProcessing(false);
        }
    };

    const copyInviteLink = (code) => {
        navigator.clipboard.writeText(`${window.location.origin}/competitions/join/${code}`);
        toast.success('Invite link copied!');
    };

    const copyFestivalLink = (code) => {
        navigator.clipboard.writeText(`${window.location.origin}/festival/join/${code}`);
        toast.success('Festival QR sign-up link copied!');
    };

    const getStatusBadge = (status) => {
        const styles = {
            draft: 'bg-gray-500/20 text-gray-400',
            open: 'bg-green-500/20 text-green-400',
            in_progress: 'bg-blue-500/20 text-blue-400',
            completed: 'bg-purple-500/20 text-purple-400'
        };
        return <Badge className={styles[status] || styles.draft}>{status?.replace('_', ' ')}</Badge>;
    };

    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-10 w-1/3 mb-8" />
                <div className="grid md:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-40" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="container-app py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-heading font-bold mb-2 page-title">Competition Admin</h1>
                    <p className="text-muted-foreground">Create and manage tipping competitions</p>
                </div>
                <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-competition-btn">
                    <Plus className="h-4 w-4" />
                    New Competition
                </Button>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Competitions List */}
                <div className="space-y-4">
                    <h2 className="font-semibold text-lg">All Competitions</h2>
                    {competitions.length === 0 ? (
                        <Card className="p-6 text-center">
                            <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-muted-foreground">No competitions yet</p>
                            <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="mt-4">
                                Create First Competition
                            </Button>
                        </Card>
                    ) : (
                        competitions.map(comp => (
                            <Card 
                                key={comp.id}
                                className={`cursor-pointer transition-colors hover:border-primary/50 ${
                                    selectedComp?.competition?.id === comp.id ? 'border-primary' : ''
                                } ${comp.is_hidden ? 'opacity-60' : ''}`}
                                onClick={() => fetchCompetitionDetail(comp.id)}
                                data-testid={`admin-comp-${comp.id}`}
                            >
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            {comp.is_hidden && <EyeOff className="h-4 w-4 text-muted-foreground" />}
                                            {comp.is_featured && <Crown className="h-4 w-4 text-amber-400" />}
                                            {comp.is_festival && <QrCode className="h-4 w-4 text-blue-400" />}
                                            {comp.name}
                                        </CardTitle>
                                        <div className="flex items-center gap-1">
                                            {comp.is_hidden && <Badge variant="secondary" className="text-xs">Hidden</Badge>}
                                            {comp.is_festival && <Badge className="text-xs bg-blue-500/20 text-blue-400">Festival</Badge>}
                                            {getStatusBadge(comp.status)}
                                        </div>
                                    </div>
                                    <CardDescription>{comp.meeting_name}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <Users className="h-4 w-4" />
                                            {comp.entry_count} entries
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Calendar className="h-4 w-4" />
                                            {comp.races?.length || 0} races
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>

                {/* Competition Detail */}
                <div className="lg:col-span-2">
                    {selectedComp ? (
                        <Card>
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            {selectedComp.competition.is_hidden && (
                                                <EyeOff className="h-5 w-5 text-muted-foreground" />
                                            )}
                                            {selectedComp.competition.name}
                                            {selectedComp.competition.is_featured && (
                                                <Crown className="h-5 w-5 text-amber-400" />
                                            )}
                                            {selectedComp.competition.is_festival && (
                                                <QrCode className="h-5 w-5 text-blue-400" />
                                            )}
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-2">
                                            {selectedComp.competition.meeting_name}
                                            {selectedComp.competition.is_hidden && (
                                                <Badge variant="secondary">Hidden from public</Badge>
                                            )}
                                            {selectedComp.competition.is_festival && (
                                                <Badge className="bg-blue-500/20 text-blue-400">Festival</Badge>
                                            )}
                                        </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {getStatusBadge(selectedComp.competition.status)}
                                        <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleVisibility(selectedComp.competition.id);
                                            }}
                                            title={selectedComp.competition.is_hidden ? "Show competition" : "Hide competition"}
                                        >
                                            {selectedComp.competition.is_hidden ? (
                                                <Eye className="h-4 w-4" />
                                            ) : (
                                                <EyeOff className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => {
                                                setFormData({
                                                    ...selectedComp.competition,
                                                    status: selectedComp.competition.status
                                                });
                                                setShowEditDialog(true);
                                            }}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => handleDelete(selectedComp.competition.id)}
                                            className="text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Visibility Warning */}
                                {selectedComp.competition.is_hidden && (
                                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4 flex items-center gap-2">
                                        <EyeOff className="h-4 w-4 text-amber-500" />
                                        <p className="text-sm text-amber-600">
                                            This competition is hidden from public view. Only users who have already joined can see it.
                                        </p>
                                    </div>
                                )}
                                
                                {/* Invite Link — festival competitions only use the QR sign-up link below */}
                                {!selectedComp.competition.is_festival && (
                                    <div className="p-3 bg-muted rounded-lg mb-4 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Invite Link</p>
                                            <code className="text-sm">/competitions/join/{selectedComp.competition.invite_code}</code>
                                        </div>
                                        <Button size="sm" variant="outline" onClick={() => copyInviteLink(selectedComp.competition.invite_code)}>
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}

                                {/* Festival QR Sign-Up Link */}
                                {selectedComp.competition.is_festival && (
                                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4 flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-blue-400 flex items-center gap-1">
                                                <QrCode className="h-3.5 w-3.5" />
                                                Festival Sign-Up Link (no login required)
                                            </p>
                                            <code className="text-sm">/festival/join/{selectedComp.competition.invite_code}</code>
                                        </div>
                                        <Button size="sm" variant="outline" onClick={() => copyFestivalLink(selectedComp.competition.invite_code)}>
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}

                                {/* Quick Actions */}
                                <div className="flex flex-wrap gap-2 mb-6">
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handleRefreshRaces(selectedComp.competition.id)}
                                        disabled={processing}
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Refresh Races
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleProcessResults(selectedComp.competition.id)}
                                        disabled={processing}
                                    >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Process Results
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleProcessResults(selectedComp.competition.id, true)}
                                        disabled={processing}
                                        className="text-amber-600 border-amber-600 hover:bg-amber-50"
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Force Re-process (+ Silks)
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleBackfillPickSilks(selectedComp.competition.id)}
                                        disabled={processing}
                                        className="text-purple-600 border-purple-600 hover:bg-purple-50"
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Update Pick Silks
                                    </Button>
                                    <Link to={`/competitions/${selectedComp.competition.id}`}>
                                        <Button variant="outline" size="sm">
                                            <Eye className="h-4 w-4 mr-2" />
                                            View as User
                                        </Button>
                                    </Link>
                                </div>

                                <Tabs defaultValue="days">
                                    <TabsList>
                                        <TabsTrigger value="days">Days ({selectedComp.competition.days?.length || 1})</TabsTrigger>
                                        <TabsTrigger value="entries">Entries ({selectedComp.entries?.length || 0})</TabsTrigger>
                                        <TabsTrigger value="races">Races ({selectedComp.competition.races?.length || 0})</TabsTrigger>
                                        <TabsTrigger value="daily-winners">Daily Winners</TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="days" className="mt-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-semibold">Competition Days</h3>
                                            <Button 
                                                size="sm" 
                                                onClick={() => setShowAddDayDialog(true)}
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add Day
                                            </Button>
                                        </div>
                                        <div className="space-y-3">
                                            {(selectedComp.competition.days?.length > 0 
                                                ? selectedComp.competition.days 
                                                : [{
                                                    day_number: 1,
                                                    date: selectedComp.competition.start_date,
                                                    meeting_name: selectedComp.competition.meeting_name,
                                                    race_count: selectedComp.competition.races?.length || 0
                                                }]
                                            ).map(day => {
                                                const dayRaces = selectedComp.competition.races?.filter(r => (r.day_number || 1) === day.day_number) || [];
                                                const finishedRaces = dayRaces.filter(r => r.status === 'finished').length;
                                                
                                                return (
                                                    <div key={day.day_number} className="p-4 rounded-lg border bg-card">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                                                                    <span className="font-bold text-primary">{day.day_number}</span>
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold">Day {day.day_number}: {day.meeting_name}</p>
                                                                    <p className="text-sm text-muted-foreground">
                                                                        {day.date ? new Date(day.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Date TBC'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <div className="text-right">
                                                                    <p className="text-sm font-medium">{dayRaces.length} races</p>
                                                                    <p className="text-xs text-muted-foreground">{finishedRaces} finished</p>
                                                                </div>
                                                                {(selectedComp.competition.days?.length || 0) > 1 && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => handleRemoveDay(day.day_number)}
                                                                        className="text-destructive"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="entries" className="mt-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-semibold">Competition Entries</h3>
                                            <Button 
                                                size="sm" 
                                                variant="outline"
                                                onClick={() => setShowAdjustDialog(true)}
                                            >
                                                <PlusCircle className="h-4 w-4 mr-2" />
                                                Adjust Score
                                            </Button>
                                        </div>
                                        <ScrollArea className="h-[300px]">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>User</TableHead>
                                                        <TableHead>Email</TableHead>
                                                        <TableHead>Joined</TableHead>
                                                        <TableHead className="text-right">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {selectedComp.entries?.map(entry => (
                                                        <TableRow key={entry.user_id}>
                                                            <TableCell className="font-medium">{entry.user_name}</TableCell>
                                                            <TableCell className="text-muted-foreground">{entry.user_email}</TableCell>
                                                            <TableCell className="text-sm">
                                                                {new Date(entry.joined_at).toLocaleDateString()}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleRemoveUser(selectedComp.competition.id, entry.user_id)}
                                                                    className="text-destructive"
                                                                >
                                                                    <UserMinus className="h-4 w-4" />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </ScrollArea>

                                        {/* Removed Users Section */}
                                        {selectedComp.removed_users_count > 0 && (
                                            <Collapsible 
                                                open={showRemovedUsers} 
                                                onOpenChange={(open) => {
                                                    setShowRemovedUsers(open);
                                                    if (open && removedUsers.length === 0) {
                                                        fetchRemovedUsers(selectedComp.competition.id);
                                                    }
                                                }}
                                                className="mt-4"
                                            >
                                                <CollapsibleTrigger asChild>
                                                    <Button variant="outline" size="sm" className="w-full justify-between">
                                                        <span className="flex items-center gap-2">
                                                            <RotateCcw className="h-4 w-4" />
                                                            Removed Users ({selectedComp.removed_users_count})
                                                        </span>
                                                        <ChevronDown className={`h-4 w-4 transition-transform ${showRemovedUsers ? 'rotate-180' : ''}`} />
                                                    </Button>
                                                </CollapsibleTrigger>
                                                <CollapsibleContent className="mt-2">
                                                    <div className="rounded-lg border bg-muted/30 p-3">
                                                        <p className="text-sm text-muted-foreground mb-3">
                                                            These users were removed but their data is preserved. Click restore to add them back.
                                                        </p>
                                                        <div className="space-y-2">
                                                            {removedUsers.map(user => (
                                                                <div 
                                                                    key={user.user_id} 
                                                                    className="flex items-center justify-between p-2 rounded bg-background"
                                                                >
                                                                    <div>
                                                                        <p className="font-medium text-sm">{user.display_name || user.email}</p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            {user.picks_count} picks preserved • Removed {user.removed_at ? new Date(user.removed_at).toLocaleDateString() : 'N/A'}
                                                                        </p>
                                                                    </div>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleRestoreUser(selectedComp.competition.id, user.user_id)}
                                                                        className="text-green-500 hover:text-green-400 hover:border-green-500"
                                                                    >
                                                                        <UserPlus className="h-4 w-4 mr-1" />
                                                                        Restore
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                            {removedUsers.length === 0 && (
                                                                <p className="text-sm text-center text-muted-foreground py-2">Loading...</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        )}
                                    </TabsContent>

                                    <TabsContent value="races" className="mt-4">
                                        {/* Refresh All Results Button */}
                                        <div className="mb-4 flex justify-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleRefreshAllResults(selectedComp.competition.id)}
                                                disabled={processing}
                                                className="text-amber-500 hover:text-amber-400 hover:border-amber-500"
                                            >
                                                <RefreshCw className={`h-4 w-4 mr-2 ${processing ? 'animate-spin' : ''}`} />
                                                Refresh All Results & Points
                                            </Button>
                                        </div>
                                        
                                        <ScrollArea className="h-[300px]">
                                            <div className="space-y-2">
                                                {selectedComp.competition.races?.map(race => (
                                                    <div key={race.event_id} className="p-3 rounded-lg bg-muted/50">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <p className="font-medium">{race.event_title}</p>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {new Date(race.race_time).toLocaleString()}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-right">
                                                                    <Badge className={
                                                                        race.status === 'finished' ? 'bg-green-500/20 text-green-400' :
                                                                        race.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                                                        'bg-gray-500/20 text-gray-400'
                                                                    }>
                                                                        {race.status}
                                                                    </Badge>
                                                                    {race.winning_entrant_name && (
                                                                        <p className="text-sm mt-1">
                                                                            <Trophy className="h-3 w-3 inline text-amber-400" /> {race.winning_entrant_name}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                {race.status !== 'finished' && (
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => handleProcessSingleRace(selectedComp.competition.id, race.event_id)}
                                                                        className="text-green-500 hover:text-green-400"
                                                                        title="Process result for this race"
                                                                    >
                                                                        <RefreshCw className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleDeleteRace(selectedComp.competition.id, race.event_id)}
                                                                    className="text-destructive hover:text-destructive"
                                                                    title="Delete race from competition"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </ScrollArea>
                                    </TabsContent>

                                    <TabsContent value="daily-winners" className="mt-4">
                                        <div className="space-y-4">
                                            {/* Group races by date and show daily winner options */}
                                            {Object.entries(
                                                selectedComp.competition.races?.reduce((acc, race) => {
                                                    const date = race.race_date;
                                                    if (!acc[date]) acc[date] = [];
                                                    acc[date].push(race);
                                                    return acc;
                                                }, {}) || {}
                                            ).map(([date, races]) => {
                                                const allFinished = races.every(r => r.status === 'finished');
                                                const dailyWinner = selectedComp.daily_winners?.find(w => w.date === date);
                                                
                                                return (
                                                    <div key={date} className="p-4 rounded-lg bg-muted/50">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="font-medium">
                                                                    {new Date(date).toLocaleDateString('en-GB', { 
                                                                        weekday: 'long', day: 'numeric', month: 'long' 
                                                                    })}
                                                                </p>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {races.length} races • {races.filter(r => r.status === 'finished').length} finished
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {dailyWinner ? (
                                                                    <>
                                                                        <Badge className="bg-amber-500/20 text-amber-400">
                                                                            <Medal className="h-3 w-3 mr-1" />
                                                                            {dailyWinner.user_name}
                                                                        </Badge>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            className="h-8 w-8 p-0 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                                                                            onClick={() => handleRemoveDailyWinner(selectedComp.competition.id, date)}
                                                                            disabled={processing}
                                                                            title="Remove winner"
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setSelectedDayForWinner(date);
                                                                            setShowDailyWinnerDialog(true);
                                                                        }}
                                                                        disabled={processing}
                                                                    >
                                                                        <Medal className="h-4 w-4 mr-2" />
                                                                        Set Winner
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="h-full flex items-center justify-center">
                            <div className="text-center p-8">
                                <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                                <p className="text-lg font-medium mb-2">Select a Competition</p>
                                <p className="text-muted-foreground">Click on a competition to view and manage its details</p>
                            </div>
                        </Card>
                    )}
                </div>
            </div>

            {/* Create Competition Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create New Competition</DialogTitle>
                        <DialogDescription>
                            Set up a new tipping competition for a race meeting
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Competition Name *</Label>
                            <Input
                                placeholder="e.g., Cheltenham Festival 2025"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                placeholder="Optional description for the competition"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Start Date *</Label>
                                <Input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => {
                                        setFormData({ ...formData, start_date: e.target.value });
                                        searchMeetings(e.target.value);
                                    }}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>End Date *</Label>
                                <Input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Select Meeting *</Label>
                            {loadingMeetings ? (
                                <Skeleton className="h-10 w-full" />
                            ) : meetings.length > 0 ? (
                                <Select
                                    value={formData.meeting_id}
                                    onValueChange={(val) => {
                                        const meeting = meetings.find(m => m.id === val);
                                        setFormData({
                                            ...formData,
                                            meeting_id: val,
                                            meeting_name: meeting?.name || ''
                                        });
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a meeting" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {meetings.map(meeting => (
                                            <SelectItem key={meeting.id} value={meeting.id}>
                                                {meeting.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                                    Select a start date to load available meetings
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label>Pick Deadline (minutes before race)</Label>
                            <Input
                                type="number"
                                min={5}
                                max={120}
                                value={formData.deadline_minutes}
                                onChange={(e) => setFormData({ ...formData, deadline_minutes: parseInt(e.target.value) })}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Featured Competition</Label>
                                <p className="text-sm text-muted-foreground">Show on homepage with countdown hero</p>
                            </div>
                            <Switch
                                checked={formData.is_featured}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Festival Mode</Label>
                                <p className="text-sm text-muted-foreground">Enable password-free QR code sign-up (name + company only)</p>
                            </div>
                            <Switch
                                checked={formData.is_festival}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_festival: checked })}
                            />
                        </div>

                        {/* Featured Countdown Settings */}
                        {formData.is_featured && (
                            <div className="space-y-4 p-4 border border-amber-500/30 rounded-lg bg-amber-500/5">
                                <Label className="text-amber-500 font-semibold">Featured Hero Settings</Label>
                                
                                <div className="space-y-2">
                                    <Label>Countdown Target Date</Label>
                                    <Input
                                        type="datetime-local"
                                        value={formData.featured_countdown_date}
                                        onChange={(e) => setFormData({ ...formData, featured_countdown_date: e.target.value })}
                                        placeholder="When does the race start?"
                                    />
                                    <p className="text-xs text-muted-foreground">Set the countdown target (e.g., first race time)</p>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Featured Description</Label>
                                    <Textarea
                                        value={formData.featured_description}
                                        onChange={(e) => setFormData({ ...formData, featured_description: e.target.value })}
                                        placeholder="Get excited! The Cheltenham Festival is the pinnacle of jump racing..."
                                        rows={4}
                                    />
                                    <p className="text-xs text-muted-foreground">Promotional text shown on the countdown hero</p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={processing}>
                            {processing ? 'Creating...' : 'Create Competition'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Competition Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Competition</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Competition Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Status</Label>
                            <Select
                                value={formData.status}
                                onValueChange={(val) => setFormData({ ...formData, status: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="open">Open</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Pick Deadline (minutes)</Label>
                            <Input
                                type="number"
                                value={formData.deadline_minutes}
                                onChange={(e) => setFormData({ ...formData, deadline_minutes: parseInt(e.target.value) })}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <Label>Featured</Label>
                            <Switch
                                checked={formData.is_featured}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <Label>Festival Mode</Label>
                                <p className="text-sm text-muted-foreground">Password-free QR code sign-up (name + company only)</p>
                            </div>
                            <Switch
                                checked={formData.is_festival}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_festival: checked })}
                            />
                        </div>

                        {/* Featured Countdown Settings */}
                        {formData.is_featured && (
                            <div className="space-y-4 p-4 border border-amber-500/30 rounded-lg bg-amber-500/5">
                                <Label className="text-amber-500 font-semibold">Featured Hero Settings</Label>
                                
                                <div className="space-y-2">
                                    <Label>Countdown Target Date</Label>
                                    <Input
                                        type="datetime-local"
                                        value={formData.featured_countdown_date || ''}
                                        onChange={(e) => setFormData({ ...formData, featured_countdown_date: e.target.value })}
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Featured Description</Label>
                                    <Textarea
                                        value={formData.featured_description || ''}
                                        onChange={(e) => setFormData({ ...formData, featured_description: e.target.value })}
                                        placeholder="Promotional text for the countdown hero..."
                                        rows={4}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                        <Button onClick={handleUpdate} disabled={processing}>
                            {processing ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Score Adjustment Dialog */}
            <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Adjust User Score</DialogTitle>
                        <DialogDescription>
                            Manually adjust a user&apos;s points (positive or negative)
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Select User</Label>
                            <Select
                                value={adjustmentData.user_id}
                                onValueChange={(val) => setAdjustmentData({ ...adjustmentData, user_id: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a user" />
                                </SelectTrigger>
                                <SelectContent>
                                    {selectedComp?.entries?.map(entry => (
                                        <SelectItem key={entry.user_id} value={entry.user_id}>
                                            {entry.user_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Points Adjustment</Label>
                            <Input
                                type="number"
                                step="0.5"
                                value={adjustmentData.points_adjustment}
                                onChange={(e) => setAdjustmentData({ 
                                    ...adjustmentData, 
                                    points_adjustment: parseFloat(e.target.value) 
                                })}
                            />
                            <p className="text-xs text-muted-foreground">
                                Use positive numbers to add points, negative to subtract
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label>Reason *</Label>
                            <Textarea
                                placeholder="Reason for adjustment (e.g., Non-runner rule applied)"
                                value={adjustmentData.reason}
                                onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancel</Button>
                        <Button onClick={handleAdjustScore} disabled={processing}>
                            {processing ? 'Adjusting...' : 'Apply Adjustment'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Day Dialog */}
            <Dialog open={showAddDayDialog} onOpenChange={setShowAddDayDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Day to Competition</DialogTitle>
                        <DialogDescription>
                            Select a date and meeting to add as a new day
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Select Date</Label>
                            <Input
                                type="date"
                                value={addDayData.date}
                                onChange={(e) => {
                                    setAddDayData({ ...addDayData, date: e.target.value, meeting_id: '', meeting_name: '' });
                                    searchMeetings(e.target.value);
                                }}
                            />
                        </div>

                        {loadingMeetings && (
                            <div className="flex items-center justify-center py-4">
                                <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                                <span className="text-sm text-muted-foreground">Loading meetings...</span>
                            </div>
                        )}

                        {meetings.length > 0 && (
                            <div className="space-y-2">
                                <Label>Select Meeting</Label>
                                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                    {meetings.map(meeting => (
                                        <div
                                            key={meeting.id}
                                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                                addDayData.meeting_id === meeting.id 
                                                    ? 'border-primary bg-primary/10' 
                                                    : 'hover:bg-muted/50'
                                            }`}
                                            onClick={() => setAddDayData({
                                                ...addDayData,
                                                meeting_id: meeting.id,
                                                meeting_name: meeting.name
                                            })}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-medium">{meeting.name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {meeting.events?.length || 0} races
                                                    </p>
                                                </div>
                                                {addDayData.meeting_id === meeting.id && (
                                                    <CheckCircle className="h-5 w-5 text-primary" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {addDayData.meeting_name && (
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <p className="text-sm">
                                    <strong>Selected:</strong> {addDayData.meeting_name} on{' '}
                                    {addDayData.date ? new Date(addDayData.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setShowAddDayDialog(false);
                            setAddDayData({ date: '', meeting_id: '', meeting_name: '' });
                            setMeetings([]);
                        }}>Cancel</Button>
                        <Button onClick={handleAddDay} disabled={processing || !addDayData.meeting_id}>
                            {processing ? 'Adding...' : 'Add Day'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Set Daily Winner Dialog */}
            <Dialog open={showDailyWinnerDialog} onOpenChange={setShowDailyWinnerDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Medal className="h-5 w-5 text-amber-400" />
                            Set Daily Winner
                        </DialogTitle>
                        <DialogDescription>
                            {selectedDayForWinner && (
                                <>Select the winner for {new Date(selectedDayForWinner).toLocaleDateString('en-GB', { 
                                    weekday: 'long', day: 'numeric', month: 'long' 
                                })}</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="py-4">
                        <Label className="mb-2 block">Select Player</Label>
                        <Select
                            value={dailyWinnerUserId}
                            onValueChange={setDailyWinnerUserId}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Choose a player..." />
                            </SelectTrigger>
                            <SelectContent>
                                {selectedComp?.entries?.map(entry => (
                                    <SelectItem key={entry.user_id} value={entry.user_id}>
                                        <div className="flex items-center justify-between w-full">
                                            <span>{entry.user_name}</span>
                                            <span className="text-muted-foreground ml-2">
                                                {entry.total_points?.toFixed(1) || 0} pts
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {selectedComp?.entries?.length === 0 && (
                            <p className="text-sm text-muted-foreground mt-2">
                                No participants in this competition yet.
                            </p>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setShowDailyWinnerDialog(false);
                            setDailyWinnerUserId('');
                            setSelectedDayForWinner(null);
                        }}>Cancel</Button>
                        <Button 
                            onClick={handleAddDailyWinner} 
                            disabled={processing || !dailyWinnerUserId}
                            className="bg-amber-500 hover:bg-amber-600"
                        >
                            {processing ? 'Setting...' : (
                                <>
                                    <Medal className="h-4 w-4 mr-2" />
                                    Set as Winner
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
